import { motion } from 'framer-motion';
import { Film, Sparkles, Users, Clock, Zap, Music } from 'lucide-react';

const Services = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const services = [
    {
      icon: Film,
      title: "30-Second Promos",
      description: "Quick, punchy videos perfect for social media teasers and announcements.",
      highlight: "From £15"
    },
    {
      icon: Sparkles,
      title: "AI Movie Experience",
      description: "Up to 5 minutes of cinematic AI-generated visuals that tell your story.",
      highlight: "Full Production"
    },
    {
      icon: Users,
      title: "Bulk Packages",
      description: "Order 5 or 10 videos at once and unlock exclusive discounts.",
      highlight: "Best Value"
    },
    {
      icon: Clock,
      title: "Quick Turnaround",
      description: "Fast delivery without compromising on quality. Your content, ready when you need it.",
      highlight: "Rapid Delivery"
    },
    {
      icon: Zap,
      title: "Custom Character",
      description: "Roxxie Vayne brings your vision to life with her powerful rock warrior presence.",
      highlight: "Unique Identity"
    },
    {
      icon: Music,
      title: "Music-First",
      description: "Perfect for artists, YouTubers, and creators releasing music regularly.",
      highlight: "For Creators"
    }
  ];

  return (
    <section 
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-void-black relative"
    >
      {/* Section Divider */}
      <div className="section-divider w-full absolute top-0" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 md:mb-24"
          {...fadeInUp}
        >
          <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4">
            What We Offer
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight uppercase text-[#EDEDED]">
            Cinematic AI Videos
          </h2>
          <p className="font-manrope text-neutral-400 mt-6 max-w-2xl mx-auto">
            Professional AI-generated promo videos featuring Roxxie Vayne — the rock warrior who brings your music to life.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              data-testid={`service-card-${index}`}
              className={`glass p-8 rounded-lg card-lift tracing-beam ${
                index === 1 ? 'lg:col-span-2 lg:row-span-1' : ''
              }`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-neon-red/10 rounded-lg border border-neon-red/20">
                  <service.icon className="w-6 h-6 text-neon-red" />
                </div>
                <div className="flex-1">
                  <span className="font-manrope text-xs tracking-widest uppercase text-neon-red">
                    {service.highlight}
                  </span>
                  <h3 className="font-syne font-bold text-xl md:text-2xl text-[#EDEDED] mt-1">
                    {service.title}
                  </h3>
                  <p className="font-manrope text-neutral-400 mt-3 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Character Description */}
        <motion.div 
          className="mt-16 md:mt-24 glass p-8 md:p-12 rounded-lg border-glow"
          {...fadeInUp}
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-2 border-neon-red/50 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_23aab0a6-b4d8-4ab8-9e19-4bd12735db59/artifacts/25srhfr5_hf_20260202_151914_84ae40de-23f1-4549-8edc-af3ba777d7b1.png"
                alt="Roxxie Vayne"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-syne font-bold text-2xl md:text-3xl text-[#EDEDED] mb-4">
                Meet <span className="text-neon-red">Roxxie Vayne</span>
              </h3>
              <p className="font-manrope text-neutral-300 leading-relaxed">
                Mid-30s female rock warrior icon. Tanned skin, hazel eyes, teased jet-black curls. 
                Black leather corset, long gothic leather coat, stiletto boots, diamond choker glowing under neon fire. 
                <span className="text-neon-red font-medium"> Confident, cinematic, powerful presence.</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
